import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Card, CardContent } from '../../ui/card';
import { Checkbox } from '../../ui/checkbox';
import { FolderPlus, Loader2, Ship, Plus, Trash2, Copy, FileText, Paperclip } from 'lucide-react';
import AnexosManager from '../../AnexosManager';
import AnexosErrorBoundary from '../../AnexosErrorBoundary';
import CurrencyInput from '../../ui/currency-input';
import { formatCurrency } from '../crmUtils';

export const CriarProjetoFullDialog = ({
  open, onOpenChange, oportunidade, fabricas, vesselModels,
  propostaVinculada, loading, onConfirm
}) => {
  const [activeTab, setActiveTab] = useState('geral');
  const [form, setForm] = useState({
    name: '', client: '', client_id: '', factory_id: '', factory_name: '',
    responsavel_tecnico: '', start_date: '', delivery_date: '',
    budget: 0, valor_venda: 0, custo_estimado_manual: 0, numero_empenho: '', google_drive_url: '', description: '',
    embarcacoes: [], anexos: [],
  });
  const [showAddEmb, setShowAddEmb] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [categoriasDisponiveis, setCategoriasDisponiveis] = useState([]);
  const [categoriasSelecionadas, setCategoriasSelecionadas] = useState([]);

  // Pre-fill from opportunity + proposal
  useEffect(() => {
    if (open && oportunidade) {
      const embarcacoes = [];

      // Extract embarcações from proposal items
      if (propostaVinculada && propostaVinculada.itens) {
        propostaVinculada.itens.forEach((item) => {
          if (item.tipo === 'embarcacao' && item.vessel_model_id) {
            const qty = item.quantidade || 1;
            for (let i = 0; i < qty; i++) {
              const model = vesselModels.find(m => m.id === item.vessel_model_id);
              const checklist = model?.checklist_base?.map(c => ({
                ...c, tipo_item: c.tipo_item || 'especificacao',
                custo_manual: 0, itens_custo: [], custo_total: 0
              })) || [];
              embarcacoes.push({
                id: crypto.randomUUID(),
                numero: embarcacoes.length + 1,
                vessel_model_id: item.vessel_model_id,
                vessel_model_nome: item.vessel_model_nome || model?.nome || '',
                status: 'planejamento', progress: 0,
                checklist, observacoes: ''
              });
            }
          }
        });
      }

      // Fallback: extract from opportunity if no proposal embarcacoes
      if (embarcacoes.length === 0 && oportunidade.embarcacoes?.length > 0) {
        oportunidade.embarcacoes.forEach(emb => {
          const qty = emb.quantidade || 1;
          for (let i = 0; i < qty; i++) {
            const model = vesselModels.find(m => m.id === emb.modelo_id);
            const checklist = model?.checklist_base?.map(c => ({
              ...c, tipo_item: c.tipo_item || 'especificacao',
              custo_manual: 0, itens_custo: [], custo_total: 0
            })) || [];
            embarcacoes.push({
              id: crypto.randomUUID(),
              numero: embarcacoes.length + 1,
              vessel_model_id: emb.modelo_id || '',
              vessel_model_nome: emb.modelo_nome || model?.nome || '',
              status: 'planejamento', progress: 0,
              checklist, observacoes: ''
            });
          }
        });
      }

      setForm({
        name: oportunidade.titulo || '',
        client: oportunidade.cliente_nome || oportunidade.contato_nome || '',
        client_id: oportunidade.cliente_id || '',
        factory_id: '', factory_name: '',
        responsavel_tecnico: '',
        start_date: new Date().toISOString().split('T')[0],
        delivery_date: '',
        budget: oportunidade.valor_final || oportunidade.valor_estimado || 0,
        valor_venda: (propostaVinculada?.status === 'aceita' ? propostaVinculada.total : null) || oportunidade.valor_final || oportunidade.valor_estimado || 0,
        custo_estimado_manual: 0,
        numero_empenho: oportunidade.licitacao_numero_processo || '',
        google_drive_url: '', description: '',
        embarcacoes,
        anexos: [],
      });
      setActiveTab('geral');
    }
  }, [open, oportunidade, propostaVinculada, vesselModels]);

  const onSelectModel = (modelId) => {
    setSelectedModelId(modelId);
    const model = vesselModels.find(m => m.id === modelId);
    if (model?.checklist_base) {
      const cats = [...new Set(model.checklist_base.map(i => i.categoria))];
      setCategoriasDisponiveis(cats);
      setCategoriasSelecionadas(cats);
    } else {
      setCategoriasDisponiveis([]);
      setCategoriasSelecionadas([]);
    }
  };

  const addEmbarcacao = () => {
    const model = vesselModels.find(m => m.id === selectedModelId);
    if (!model) return;
    const checklist = model.checklist_base
      ?.filter(i => categoriasSelecionadas.includes(i.categoria))
      .map(i => ({ ...i, tipo_item: i.tipo_item || 'especificacao', custo_manual: 0, itens_custo: [], custo_total: 0 })) || [];
    const newEmb = {
      id: crypto.randomUUID(),
      numero: form.embarcacoes.length + 1,
      vessel_model_id: model.id,
      vessel_model_nome: model.nome,
      status: 'planejamento', progress: 0,
      checklist, categorias_selecionadas: categoriasSelecionadas,
      observacoes: ''
    };
    setForm(f => ({ ...f, embarcacoes: [...f.embarcacoes, newEmb] }));
    setShowAddEmb(false);
    setSelectedModelId('');
  };

  const removeEmbarcacao = (idx) => {
    const updated = form.embarcacoes.filter((_, i) => i !== idx);
    updated.forEach((e, i) => e.numero = i + 1);
    setForm(f => ({ ...f, embarcacoes: updated }));
  };

  const duplicarEmbarcacao = (idx) => {
    const emb = form.embarcacoes[idx];
    const nova = { ...emb, id: crypto.randomUUID(), numero: form.embarcacoes.length + 1, checklist: emb.checklist?.map(c => ({ ...c })) || [] };
    setForm(f => ({ ...f, embarcacoes: [...f.embarcacoes, nova] }));
  };

  const handleConfirm = () => {
    if (!form.name.trim()) return;
    const fab = fabricas.find(f => f.id === form.factory_id);
    onConfirm({
      ...form,
      budget: form.valor_venda || form.budget || 0,
      factory_name: fab?.name || '',
      auto_attach_pdf: !!propostaVinculada,
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="criar-projeto-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="h-5 w-5 text-blue-600" />
              Criar Projeto a partir da Venda
            </DialogTitle>
          </DialogHeader>

          {oportunidade && (
            <div className="bg-slate-50 rounded-lg p-3 text-sm border">
              <div className="font-medium text-slate-700">Oportunidade: {oportunidade.titulo}</div>
              <div className="text-slate-500 flex flex-wrap gap-x-3">
                <span>Cliente: {oportunidade.cliente_nome || oportunidade.contato_nome}</span>
                {propostaVinculada && (
                  <span className="text-green-600 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    Proposta N {propostaVinculada.numero} ({propostaVinculada.status})
                  </span>
                )}
              </div>
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="geral">Dados Gerais</TabsTrigger>
              <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
              <TabsTrigger value="embarcacoes">Embarcacoes ({form.embarcacoes.length})</TabsTrigger>
              <TabsTrigger value="anexos" className="flex items-center gap-1">
                <Paperclip className="h-3 w-3" />
                Anexos ({(form.anexos?.length || 0) + (propostaVinculada ? 1 : 0)})
              </TabsTrigger>
            </TabsList>

            {/* Tab Dados Gerais */}
            <TabsContent value="geral" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome do Projeto *</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Projeto T800 - Marinha" data-testid="projeto-nome" />
                </div>
                <div>
                  <Label>Cliente *</Label>
                  <Input value={form.client} onChange={e => setForm(f => ({ ...f, client: e.target.value }))} placeholder="Nome do cliente" />
                  {form.client_id && <div className="text-xs text-green-600 mt-0.5">Vinculado ao CRM</div>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data de Inicio</Label>
                  <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
                </div>
                <div>
                  <Label>Data de Entrega *</Label>
                  <Input type="date" value={form.delivery_date} onChange={e => setForm(f => ({ ...f, delivery_date: e.target.value }))} data-testid="projeto-delivery" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fabrica</Label>
                  <Select value={form.factory_id || 'none'} onValueChange={v => setForm(f => ({ ...f, factory_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Selecionar fabrica</SelectItem>
                      {fabricas.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>N Empenho / Contrato</Label>
                  <Input value={form.numero_empenho} onChange={e => setForm(f => ({ ...f, numero_empenho: e.target.value }))} placeholder="2025/001" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Responsavel Tecnico</Label>
                  <Input value={form.responsavel_tecnico} onChange={e => setForm(f => ({ ...f, responsavel_tecnico: e.target.value }))} placeholder="Nome do responsavel" />
                </div>
              </div>
              <div>
                <Label>Link Google Drive</Label>
                <Input value={form.google_drive_url} onChange={e => setForm(f => ({ ...f, google_drive_url: e.target.value }))} placeholder="https://drive.google.com/..." />
              </div>
              <div>
                <Label>Descricao</Label>
                <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} />
              </div>
            </TabsContent>

            {/* Tab Financeiro */}
            <TabsContent value="financeiro" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valor de Venda (R$)</Label>
                  <CurrencyInput value={form.valor_venda} onChange={v => setForm(f => ({ ...f, valor_venda: v }))} data-testid="projeto-valor-venda" />
                  {propostaVinculada?.status === 'aceita' && (
                    <p className="text-xs text-green-600 mt-0.5">Preenchido pela proposta N {propostaVinculada.numero}</p>
                  )}
                </div>
                <div>
                  <Label>Custo Estimado (R$)</Label>
                  <CurrencyInput value={form.custo_estimado_manual} onChange={v => setForm(f => ({ ...f, custo_estimado_manual: v }))} data-testid="projeto-custo-estimado" />
                </div>
              </div>
              <p className="text-xs text-gray-500">O Custo Efetivo (soma dos Pedidos de Compra aprovados) e o historico de pedidos ficam disponiveis na aba Financeiro do projeto apos ele ser criado.</p>
            </TabsContent>

            {/* Tab Embarcacoes */}
            <TabsContent value="embarcacoes" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">{form.embarcacoes.length} embarcacao(oes) no projeto</p>
                <Button type="button" size="sm" onClick={() => { setShowAddEmb(true); setSelectedModelId(''); }} className="gap-2">
                  <Plus className="h-4 w-4" />Adicionar Embarcacao
                </Button>
              </div>

              {form.embarcacoes.length === 0 ? (
                <Card><CardContent className="flex flex-col items-center justify-center h-24 text-gray-500">
                  <Ship className="h-8 w-8 mb-2 opacity-30" />
                  <p className="text-sm">Nenhuma embarcacao adicionada</p>
                  {propostaVinculada && <p className="text-xs text-amber-600 mt-1">Nenhum item de embarcacao encontrado na proposta</p>}
                </CardContent></Card>
              ) : (
                <div className="space-y-2">
                  {form.embarcacoes.map((emb, idx) => (
                    <Card key={emb.id}>
                      <CardContent className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded bg-blue-100 flex items-center justify-center">
                            <Ship className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">Embarcacao #{emb.numero}</p>
                            <p className="text-xs text-gray-500">
                              {emb.vessel_model_nome || 'Modelo nao definido'}
                              {emb.checklist?.length > 0 && ` - ${emb.checklist.length} itens`}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button type="button" size="sm" variant="ghost" onClick={() => duplicarEmbarcacao(idx)} title="Duplicar">
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={() => removeEmbarcacao(idx)} title="Remover">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Tab Anexos */}
            <TabsContent value="anexos" className="space-y-4 mt-4">
              {propostaVinculada && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
                  <FileText className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800">PDF da Proposta N {propostaVinculada.numero}</p>
                    <p className="text-xs text-green-600">Sera gerado e anexado automaticamente ao criar o projeto</p>
                  </div>
                </div>
              )}
              <AnexosErrorBoundary>
                <AnexosManager
                  anexos={form.anexos || []}
                  onChange={(novos) => setForm(f => ({ ...f, anexos: novos }))}
                  maxFiles={20} maxSizeMB={50}
                  label="Documentos adicionais do Projeto"
                  modulo="projetos"
                  entidadeId={form.id || 'novo'}
                  categoria="anexos"
                />
              </AnexosErrorBoundary>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleConfirm} disabled={loading || !form.name.trim()} data-testid="confirmar-criar-projeto">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FolderPlus className="h-4 w-4 mr-2" />}
              Criar Projeto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-dialog: Adicionar Embarcacao */}
      <Dialog open={showAddEmb} onOpenChange={setShowAddEmb}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adicionar Embarcacao</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Modelo de Embarcacao *</Label>
              <Select value={selectedModelId || 'none'} onValueChange={v => v !== 'none' && onSelectModel(v)}>
                <SelectTrigger><SelectValue placeholder="Selecione um modelo..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Selecione...</SelectItem>
                  {vesselModels.map(m => <SelectItem key={m.id} value={m.id}>{m.nome} ({m.checklist_base?.length || 0} itens)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {categoriasDisponiveis.length > 0 && (
              <div>
                <Label className="mb-2 block">Categorias do Checklist</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {categoriasDisponiveis.map(cat => (
                    <div key={cat} className="flex items-center gap-2">
                      <Checkbox checked={categoriasSelecionadas.includes(cat)}
                        onCheckedChange={checked => {
                          if (checked) setCategoriasSelecionadas(p => [...p, cat]);
                          else setCategoriasSelecionadas(p => p.filter(c => c !== cat));
                        }} />
                      <Label className="text-sm">{cat}</Label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">{categoriasSelecionadas.length} de {categoriasDisponiveis.length} categorias</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEmb(false)}>Cancelar</Button>
            <Button onClick={addEmbarcacao} disabled={!selectedModelId}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
